import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { Button, DisplayMessage, Icon, Tooltip, Typography } from '@gbm/queen-ui-guidelines';

import IconDelete from 'components/IconDelete';
import { NavLink, useHistory } from 'react-router-dom';
import { ApplicationModal } from './ApplicationModal';

import styles from './styles.module.scss';
import { ApplicationDeleteDialog } from '../Dialogs/ApplicationDeleteDialog';
import { ApplicationDeleteConfirmation } from '../Dialogs/ApplicationDeleteConfirmation';

import { useDispatcherApplication } from 'providers/AccessManagementApplication/useDispatcher';
import { useSelectorApplication } from 'providers/AccessManagementApplication/useSelectors';

function Applications({ applications, small }) {
  const history = useHistory();
  const [isOpenModalApp, setIsOpenModalApp] = useState(false);
  const [isOpenDialogDeleteApp, setIsOpenDialogDeleteApp] = useState(false);
  const [isOpenConfirmationDeleteApp, setIsOpenConfirmationDeleteApp] = useState(false);
  const [appToEdit, setAppToEdit] = useState(null);
  const [appToDelete, setAppToDelete] = useState(null);
  const [pendingDeletedApp, setPendingDeletedApp] = useState(null);
  const [shouldSelectFirstAfterDelete, setShouldSelectFirstAfterDelete] = useState(false);
  const [updatedDisplayName, setUpdatedDisplayName] = useState('');
  const { applicationsState, selectedApplicationState } = useSelectorApplication();
  const activeAppId = selectedApplicationState.app?.sk ?? null;

  const {
    getApplications,
    createApplication,
    updateApplication,
    deleteApplication,
    selectedApplication,
    resetCreateApplication,
    resetUpdateApplication,
    resetDeleteApplication,
  } = useDispatcherApplication();

  const { createApplicationState, updateApplicationState, deleteApplicationState } = useSelectorApplication();

  useEffect(() => {
    return () => {
      resetCreateApplication();
      resetUpdateApplication();
      resetDeleteApplication();
    };
  }, [resetCreateApplication, resetUpdateApplication, resetDeleteApplication]);

  const handleOpenModalCreateApp = () => {
    resetCreateApplication();
    resetUpdateApplication();
    setIsOpenModalApp(true);
    setAppToEdit(null);
  };

  const handleOpenModalToEditApp = app => {
    resetCreateApplication();
    resetUpdateApplication();
    setIsOpenModalApp(true);
    setAppToEdit(app);
  };

  const handleCloseModalApp = () => {
    setIsOpenModalApp(false);
    setAppToEdit(null);
    setUpdatedDisplayName('');
    resetCreateApplication();
    resetUpdateApplication();
  };

  const handleOpenDialogToDeleteApp = app => {
    resetDeleteApplication();
    setAppToDelete(app);
    setIsOpenDialogDeleteApp(true);
  };

  const handleOpenConfirmationDeleteApp = e => {
    e.preventDefault();
    setIsOpenDialogDeleteApp(false);
    setIsOpenConfirmationDeleteApp(true);
  };

  const handleCloseConfirmationDeleteApp = () => {
    setIsOpenConfirmationDeleteApp(false);
  };

  const handleCreateApp = ({ gbmPodId, clientId, app, displayName, type }) => {
    createApplication({ gbmPodId, clientId, app, displayName, type: type?.value || type });
  };

  const handleEditApp = ({ displayName }) => {
    setUpdatedDisplayName(displayName);
    updateApplication(appToEdit.id, { displayName });
  };

  const handleAppSelected = app => {
    selectedApplication(app);
  };

  useEffect(() => {
    if (createApplicationState.loaded) {
      setIsOpenModalApp(false);
      getApplications();
      resetCreateApplication();
    }
  }, [createApplicationState.loaded, getApplications, resetCreateApplication]);

  useEffect(() => {
    if (updateApplicationState.loaded) {
      setIsOpenModalApp(false);

      if (appToEdit && updatedDisplayName) {
        const currentSelectedApp = selectedApplicationState.app || {};
        const isEditingSelectedApp =
          currentSelectedApp.id === appToEdit.id ||
          currentSelectedApp.sk === appToEdit.sk ||
          currentSelectedApp.app === appToEdit.app;

        if (isEditingSelectedApp) {
          selectedApplication({
            ...currentSelectedApp,
            displayName: updatedDisplayName,
            display_name: updatedDisplayName,
          });
        }
      }

      setUpdatedDisplayName('');
      setAppToEdit(null);
      resetUpdateApplication();
    }
  }, [
    updateApplicationState.loaded,
    appToEdit,
    updatedDisplayName,
    selectedApplicationState.app,
    selectedApplication,
    resetUpdateApplication,
  ]);

  useEffect(() => {
    if (deleteApplicationState.loaded) {
      setIsOpenConfirmationDeleteApp(false);
      getApplications();
      setPendingDeletedApp(appToDelete);
      setShouldSelectFirstAfterDelete(true);
    }
  }, [deleteApplicationState.loaded, getApplications, appToDelete]);

  useEffect(() => {
    if (deleteApplicationState.error) {
      setIsOpenConfirmationDeleteApp(false);
      setIsOpenDialogDeleteApp(false);
      setShouldSelectFirstAfterDelete(false);
      setPendingDeletedApp(null);
      setAppToDelete(null);
    }
  }, [deleteApplicationState.error]);

  useEffect(() => {
    if (!shouldSelectFirstAfterDelete || !applicationsState.loaded || applicationsState.loading) {
      return;
    }

    const visibleApps = applications.filter(app => {
      const id = pendingDeletedApp?.id && app.id === pendingDeletedApp.id;
      const sk = pendingDeletedApp?.sk && app.sk === pendingDeletedApp.sk;
      const appName = pendingDeletedApp?.app && app.app === pendingDeletedApp.app;

      return app.to !== undefined && app.hasPermission && !id && !sk && !appName;
    });

    if (visibleApps.length > 0) {
      const firstApp = visibleApps[0];
      selectedApplication(firstApp);
      history.push(firstApp.to);
    }

    setShouldSelectFirstAfterDelete(false);
    setPendingDeletedApp(null);
    resetDeleteApplication();
  }, [
    shouldSelectFirstAfterDelete,
    applicationsState.loaded,
    applicationsState.loading,
    applications,
    pendingDeletedApp,
    selectedApplication,
    history,
    resetDeleteApplication,
  ]);

  const renderListApplications = () => {
    if (!applications.length) {
      return (
        <DisplayMessage id="msgApplicationsEmptyInfo" variant="info" message="There are not applications configured" />
      );
    }
    const visibleApps = applications.filter(app => app.to !== undefined && app.hasPermission);

    return (
      <ul className="nav">
        {visibleApps.map((app, i) => {
          const isSelectedApp = activeAppId === app.sk;

          return (
            <li key={app.sk ?? i} className={clsx(styles.sidebarLabel, { [styles.sidebarLabelSmall]: small })}>
              <NavLink
                exact
                to={app.to}
                activeClassName={activeAppId === app.sk ? 'active' : 'inactive'}
                className={styles.content}
                title={app.application}
                onClick={() => handleAppSelected(app)}>
                {app.application}
              </NavLink>
              <div className={styles.actions}>
                <Tooltip message="Edit" position="top">
                  <Button
                    id={'btnEditApp'}
                    data-testid={'btnEditApp'}
                    icon
                    size="xs"
                    color="light"
                    disabled={!isSelectedApp}
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenModalToEditApp(app);
                    }}>
                    <Icon className="remixicon-pencil-fill" />
                  </Button>
                </Tooltip>
                <Tooltip message="Delete" position="top">
                  <Button
                    id={'btnDeleteApp'}
                    data-testid={'btnDeleteApp'}
                    icon
                    size="xs"
                    color="light"
                    disabled={!isSelectedApp}
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenDialogToDeleteApp(app);
                    }}>
                    <IconDelete />
                  </Button>
                </Tooltip>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <div className={styles.sidebarTitle}>
        <Typography component="h4">{'Applications'}</Typography>
        <Button bordered={false} color="info" icon size={'xs'} type="button" onClick={handleOpenModalCreateApp}>
          <Icon className="fas fa-plus" component="i" />
        </Button>
      </div>
      {renderListApplications()}

      {isOpenModalApp && (
        <ApplicationModal
          open={isOpenModalApp}
          onClose={handleCloseModalApp}
          title={appToEdit ? 'Edit application' : 'Create application'}
          applications={applicationsState.list}
          appToEdit={appToEdit}
          onSubmit={appToEdit ? handleEditApp : handleCreateApp}
          loading={appToEdit ? updateApplicationState.loading : createApplicationState.loading}
          error={Boolean(appToEdit ? updateApplicationState.error : createApplicationState.error)}
        />
      )}

      {isOpenDialogDeleteApp && (
        <ApplicationDeleteDialog
          isOpen={isOpenDialogDeleteApp}
          onClose={() => setIsOpenDialogDeleteApp(false)}
          onConfirm={handleOpenConfirmationDeleteApp}
          application={appToDelete}
        />
      )}

      {isOpenConfirmationDeleteApp && (
        <ApplicationDeleteConfirmation
          isOpen={isOpenConfirmationDeleteApp}
          onClose={handleCloseConfirmationDeleteApp}
          onConfirm={() => deleteApplication(appToDelete.id)}
          loading={deleteApplicationState.loading}
          error={deleteApplicationState.error}
          application={appToDelete}
        />
      )}
    </>
  );
}

Applications.propTypes = {
  applications: PropTypes.array,
  small: PropTypes.bool,
  onCreateApplication: PropTypes.func,
  onEditApplication: PropTypes.func,
  onDeleteApplication: PropTypes.func,
};

export default Applications;
